import { ReactElement, use } from 'react';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import PersonalNumberRangeRowContext from 'features/care-connect-settings/contexts/personal-number-range-row-context';
import { PersonalNumberRange } from 'features/care-connect-settings/domain/models/personal-number-range';
import usePersonalNumberValidation from 'features/care-connect-settings/hooks/personal-number-validation';
import styles from './personal-number-range-row-upsert.module.scss';

const PersonalNumberRangeRowFields = ({ personalNumberRange }: { personalNumberRange: PersonalNumberRange }): ReactElement => {
  const { t } = useTranslation('careConnectSettings');
  const { control } = useFormContext<PersonalNumberRange>();
  const { validationRules, handleDigitsOnlyChange } = usePersonalNumberValidation(personalNumberRange);

  return (
    <div className={styles.fieldsRow}>
      <Controller
        name="lowerBound"
        control={control}
        rules={validationRules.lowerBound}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label={t('personalNumberRangeDetails.lowerBoundLabel')}
            variant="outlined"
            fullWidth
            error={!!error}
            helperText={error?.message ?? ''}
            value={field.value ?? ''}
            slotProps={{ htmlInput: { maxLength: 5 } }}
            onChange={(e) => handleDigitsOnlyChange(e.target.value, field.onChange)}
          />
        )}
      />
      <Controller
        name="upperBound"
        control={control}
        rules={validationRules.upperBound}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label={t('personalNumberRangeDetails.upperBoundLabel')}
            variant="outlined"
            fullWidth
            error={!!error}
            helperText={error?.message ?? ''}
            value={field.value ?? ''}
            slotProps={{ htmlInput: { maxLength: 5 } }}
            onChange={(e) => handleDigitsOnlyChange(e.target.value, field.onChange)}
          />
        )}
      />
    </div>
  );
};

const PersonalNumberRangeRowUpsert = (): ReactElement => {
  const { personalNumberRange, form, submitForm, cancelCreate, stopEditing, viewingMode } = use(PersonalNumberRangeRowContext);

  const isEditing = viewingMode === 'editing';

  return (
    <div className={styles.container}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submitForm)}>
          <div className={styles.inputFields}>
            <PersonalNumberRangeRowFields personalNumberRange={personalNumberRange} />
          </div>
          <div className={styles.buttonsContainer}>
            <IconButton className={styles.cancelButton} onClick={() => (isEditing ? stopEditing() : cancelCreate(personalNumberRange))}>
              <CloseRoundedIcon />
            </IconButton>
            <IconButton className={styles.saveButton} type="submit">
              <CheckRoundedIcon />
            </IconButton>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default PersonalNumberRangeRowUpsert;
